// Pure helpers of the Email Automations screens (list, builder and their modals)

export const AUTOMATION_PRODUCTS = ["push", "pull", "press", "perform"];

/** "Franco Alvarez" as the coach filter shows it: "Franco A." */
export const shortName = (name) => {
  const words = String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length < 2) return words[0] ?? "";
  return `${words[0]} ${words.at(-1)[0].toUpperCase()}.`;
};

/** Extra tokens a PUSH trigger always shows, besides its settings and filters. */
const pushNotes = (automation, filters) => {
  if (automation.product !== "push") return [];
  if (automation.trigger.key === "trial_ending") return [{ type: "trialOnly" }];
  if ("billingFrequency" in filters) return [];
  return [{ type: "allBillingFrequencies" }];
};

/**
 * What the second line of an automation card says about its trigger, as tokens the card turns
 * into text: the settings of the trigger (days before...) and the values of its filters. A
 * membership trigger with no membership filter reads "all memberships".
 * @param {{trigger: {key: string, settings?: object}, filters?: object}} automation
 * @param {{membershipTypes?: Array, locations?: Array, coaches?: Array}} [options] to name the ids
 * @returns {Array<{type: string, [key: string]: *}>}
 */
export const describeTrigger = (automation, options = {}) => {
  const tokens = [];
  const { settings = {} } = automation.trigger;
  for (const [key, value] of Object.entries(settings)) {
    if (typeof value === "number" && value > 0) {
      tokens.push({ type: "setting", key, value });
    } else if (value === true) {
      tokens.push({ type: "flag", key });
    }
  }

  const namesById = {
    membershipType: options.membershipTypes,
    location: options.locations,
    coach: options.coaches,
  };
  const filters = automation.filters ?? {};
  for (const [field, values] of Object.entries(filters)) {
    const known = namesById[field];
    tokens.push({
      type: "filter",
      field,
      values,
      names: known
        ? values.map((value) => {
            const name = known.find((item) => item.id === value)?.name ?? value;
            return field === "coach" ? shortName(name) : name;
          })
        : null,
    });
  }
  if (!("membershipType" in filters) && automation.product === "press") {
    tokens.push({ type: "allMemberships" });
  }
  if (
    automation.product === "pull" &&
    automation.trigger.key === "stopped_training"
  ) {
    tokens.push({ type: "recheck" });
  }
  tokens.push(...pushNotes(automation, filters));
  return tokens;
};

/**
 * The little tags under the trigger of a card: Email, its kind and how many emails there are.
 * @returns {Array<{type: "email"|"transactional"|"marketing"|"updateCardLink"|"downloadLinkCode"|"emails", count?: number}>}
 */
export const describeEmails = (automation) => {
  if (!automation.emailCount) return [];
  const tags = [{ type: "email" }];
  if (automation.hasTransactional) tags.push({ type: "transactional" });
  if (automation.hasMarketing) tags.push({ type: "marketing" });
  if (automation.hasUpdateCardLink) tags.push({ type: "updateCardLink" });
  if (automation.hasInviteCode) tags.push({ type: "downloadLinkCode" });
  if (automation.emailCount > 1) {
    tags.push({ type: "emails", count: automation.emailCount });
  }
  return tags;
};

/** Open rate as a whole percentage, or null when nothing was sent. */
export const openRatePercent = (automation) =>
  automation.sent30 > 0 && automation.openRate30 != null
    ? Math.round(automation.openRate30 * 100)
    : null;

/** Whether the toggle of a card shows as on. */
export const isOn = (automation) => automation.status === "active";

/**
 * The product the page works on: the one asked for if it is available, else the first one.
 * @param {string|undefined} wanted
 * @param {string[]} available
 */
export const pickProduct = (wanted, available) =>
  available.includes(wanted) ? wanted : (available[0] ?? null);

/**
 * The triggers of every product, one entry per trigger (key and name) with the products that have
 * it: "Upcoming charge" is PUSH · PRESS.
 * @param {Array<{key: string, name: string, product: string}>} triggers
 */
export const groupTriggers = (triggers, preferred) => {
  const groups = new Map();
  for (const trigger of triggers) {
    const key = `${trigger.key}|${trigger.name}`;
    const group = groups.get(key) ?? { ...trigger, products: [] };
    group.products.push(trigger.product);
    // A trigger of several products reads (and sorts) as in the product being viewed
    if (trigger.product === preferred) {
      Object.assign(group, { ...trigger, products: group.products });
    }
    groups.set(key, group);
  }
  const list = [...groups.values()];
  if (!preferred) return list;
  const rank = (group) => {
    const index = triggers.findIndex(
      (trigger) =>
        trigger.product === preferred &&
        trigger.key === group.key &&
        trigger.name === group.name
    );
    return index === -1 ? Number.POSITIVE_INFINITY : index;
  };
  return list.sort((a, b) => {
    const [rankA, rankB] = [rank(a), rank(b)];
    if (rankA === rankB) return 0;
    return rankA < rankB ? -1 : 1;
  });
};

/**
 * The parts of an automation (or a template) as the little chips of a flow: its trigger, the
 * filter on the trigger if it has one, then every step in order.
 * @param {{filters?: object, steps?: Array<{type: string, config?: object}>}} automation
 * @returns {Array<{kind: "trigger"|"filter"|"email"|"delay", amount?: number, unit?: string}>}
 */
export const flowParts = ({ filters, steps = [] }) => [
  { kind: "trigger" },
  ...(Object.keys(filters ?? {}).length > 0 ? [{ kind: "filter" }] : []),
  ...steps.map((step) =>
    step.type === "delay"
      ? { kind: "delay", amount: step.config?.amount, unit: step.config?.unit }
      : { kind: step.type }
  ),
];
